import dayjs from 'dayjs';

import {
    DatePeriodEnum,
    DateRangeType,
    endOfUtcDay,
    getAnalyticsRangeByPeriodAndDate,
    getDaysBetween,
    getIsoDateWithTimezone,
    startOfUtcDay,
} from '../../../util/date';
import {getEnumValueEnsure} from '../../../util/enum';
import {NullableType} from '../../../util/type';

import {defaultReviewFilters} from './reviews-and-answers-statistics-const';
import {
    ReviewsAndAnswersCompareEnum,
    ReviewsAndAnswersFilterQueriesEnum,
    ReviewsAndAnswersFilterQueriesType,
    ReviewsAndAnswersFilterStateType,
    ReviewsAndAnswersReviewFiltersType,
} from './reviews-and-answers-statistics-filter-type';

export function getComparePeriod(
    mainPeriod: DateRangeType,
    period: DatePeriodEnum,
    compareMode: ReviewsAndAnswersCompareEnum | null
): NullableType<DateRangeType> {
    if (!compareMode) {
        return {
            start: null,
            end: null,
        };
    }

    switch (true) {
        case compareMode === ReviewsAndAnswersCompareEnum.PreviousYear: {
            return {
                start: dayjs(mainPeriod.start).subtract(1, 'year').toDate(),
                end: dayjs(mainPeriod.end).subtract(1, 'year').toDate(),
            };
        }
        case compareMode === ReviewsAndAnswersCompareEnum.PreviousPeriod && period !== DatePeriodEnum.Custom: {
            return getAnalyticsRangeByPeriodAndDate(dayjs(mainPeriod.start).subtract(1, 'day').toDate(), period);
        }
        default: {
            const countDays = getDaysBetween(mainPeriod.start, mainPeriod.end);

            return {
                start: dayjs(mainPeriod.start)
                    .subtract(countDays + 1, 'days')
                    .toDate(),
                end: dayjs(mainPeriod.end)
                    .subtract(countDays + 1, 'days')
                    .toDate(),
            };
        }
    }
}

export function getInitialPeriod(periodRaw: string | void): DatePeriodEnum {
    return getEnumValueEnsure<DatePeriodEnum>(DatePeriodEnum, periodRaw, DatePeriodEnum.Month);
}

// Maps the review-filter subset of the state to backend query params (BST-10425).
// Names match the reviews list `ReviewFilter` 1:1. Empty groups are omitted.
function reviewFiltersToApiValues(filter: ReviewsAndAnswersFilterStateType): Record<string, string> {
    const catalogIds = filter[ReviewsAndAnswersFilterQueriesEnum.CatalogIds];
    const sentiments = filter[ReviewsAndAnswersFilterQueriesEnum.Sentiments];
    const tagIds = filter[ReviewsAndAnswersFilterQueriesEnum.TagIds];
    const rating = filter[ReviewsAndAnswersFilterQueriesEnum.Rating];
    const withoutText = filter[ReviewsAndAnswersFilterQueriesEnum.WithoutText];
    const isReplied = filter[ReviewsAndAnswersFilterQueriesEnum.IsReplied];
    const actionReply = filter[ReviewsAndAnswersFilterQueriesEnum.ActionReply];
    const target = filter[ReviewsAndAnswersFilterQueriesEnum.Target];
    const includeDeleted = filter[ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted];

    return {
        ...(catalogIds.length > 0 ? {catalog_ids: catalogIds.join(',')} : {}),
        ...(sentiments.length > 0 ? {sentiments: sentiments.join(',')} : {}),
        ...(tagIds.length > 0 ? {tag_ids: tagIds.join(',')} : {}),
        ...(rating.length > 0 ? {rating: rating.join(',')} : {}),
        ...(withoutText ? {without_text: withoutText} : {}),
        ...(isReplied ? {is_replied: isReplied} : {}),
        ...(actionReply ? {action_reply: actionReply} : {}),
        ...(target ? {target} : {}),
        // on → count deleted (in_trash=true); off/default → exclude deleted (in_trash=false).
        ...(includeDeleted ? {in_trash: 'true'} : {in_trash: 'false'}),
    };
}

export function reviewsAndAnswersApiFilterValues(
    filter: ReviewsAndAnswersFilterStateType,
    mainFilterKey: string
): Record<string, string> {
    return {
        start_date: getIsoDateWithTimezone(
            startOfUtcDay(new Date(filter[ReviewsAndAnswersFilterQueriesEnum.PeriodStart]))
        ),
        end_date: getIsoDateWithTimezone(endOfUtcDay(new Date(filter[ReviewsAndAnswersFilterQueriesEnum.PeriodEnd]))),
        ...(filter[ReviewsAndAnswersFilterQueriesEnum.ComparePeriodStart]
            ? {
                  compared_start_date: getIsoDateWithTimezone(
                      startOfUtcDay(new Date(filter[ReviewsAndAnswersFilterQueriesEnum.ComparePeriodStart]))
                  ),
              }
            : {}),
        ...(filter[ReviewsAndAnswersFilterQueriesEnum.ComparePeriodEnd]
            ? {
                  compared_end_date: getIsoDateWithTimezone(
                      endOfUtcDay(new Date(filter[ReviewsAndAnswersFilterQueriesEnum.ComparePeriodEnd]))
                  ),
              }
            : {}),
        ...reviewFiltersToApiValues(filter),
        filter_key: mainFilterKey,
    };
}

// URL query values arrive as `string | void` from the url hook.
function parseCsvNumbers(raw: string | void): Array<number> {
    return raw
        ? raw
              .split(',')
              .map(Number)
              .filter((item) => Number.isFinite(item))
        : [];
}

function parseCsvStrings(raw: string | void): Array<string> {
    return raw ? raw.split(',').filter(Boolean) : [];
}

function asString(raw: string | void): string {
    return raw || '';
}

// Restores the review-filter subset from URL query params on page load.
export function getReviewFiltersFromQueries(
    queries: Partial<Record<ReviewsAndAnswersFilterQueriesEnum, string | void>>
): ReviewsAndAnswersReviewFiltersType {
    return {
        [ReviewsAndAnswersFilterQueriesEnum.CatalogIds]: parseCsvNumbers(
            queries[ReviewsAndAnswersFilterQueriesEnum.CatalogIds]
        ),
        [ReviewsAndAnswersFilterQueriesEnum.Sentiments]: parseCsvStrings(
            queries[ReviewsAndAnswersFilterQueriesEnum.Sentiments]
        ),
        [ReviewsAndAnswersFilterQueriesEnum.TagIds]: parseCsvNumbers(
            queries[ReviewsAndAnswersFilterQueriesEnum.TagIds]
        ),
        [ReviewsAndAnswersFilterQueriesEnum.Rating]: parseCsvNumbers(
            queries[ReviewsAndAnswersFilterQueriesEnum.Rating]
        ),
        [ReviewsAndAnswersFilterQueriesEnum.WithoutText]: asString(
            queries[ReviewsAndAnswersFilterQueriesEnum.WithoutText]
        ),
        [ReviewsAndAnswersFilterQueriesEnum.IsReplied]: asString(queries[ReviewsAndAnswersFilterQueriesEnum.IsReplied]),
        [ReviewsAndAnswersFilterQueriesEnum.ActionReply]: asString(
            queries[ReviewsAndAnswersFilterQueriesEnum.ActionReply]
        ),
        [ReviewsAndAnswersFilterQueriesEnum.Target]: asString(queries[ReviewsAndAnswersFilterQueriesEnum.Target]),
        // Deleted are excluded by default; only the explicit 'true' string includes them.
        [ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted]:
            queries[ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted] === 'true',
    };
}

// Serializes the review-filter subset to URL query strings (CSV / bool string / '').
export function reviewFiltersToQueries(
    reviewFilters: ReviewsAndAnswersReviewFiltersType
): Pick<
    ReviewsAndAnswersFilterQueriesType,
    | ReviewsAndAnswersFilterQueriesEnum.CatalogIds
    | ReviewsAndAnswersFilterQueriesEnum.Sentiments
    | ReviewsAndAnswersFilterQueriesEnum.TagIds
    | ReviewsAndAnswersFilterQueriesEnum.Rating
    | ReviewsAndAnswersFilterQueriesEnum.WithoutText
    | ReviewsAndAnswersFilterQueriesEnum.IsReplied
    | ReviewsAndAnswersFilterQueriesEnum.ActionReply
    | ReviewsAndAnswersFilterQueriesEnum.Target
    | ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted
> {
    return {
        [ReviewsAndAnswersFilterQueriesEnum.CatalogIds]:
            reviewFilters[ReviewsAndAnswersFilterQueriesEnum.CatalogIds].join(','),
        [ReviewsAndAnswersFilterQueriesEnum.Sentiments]:
            reviewFilters[ReviewsAndAnswersFilterQueriesEnum.Sentiments].join(','),
        [ReviewsAndAnswersFilterQueriesEnum.TagIds]: reviewFilters[ReviewsAndAnswersFilterQueriesEnum.TagIds].join(','),
        [ReviewsAndAnswersFilterQueriesEnum.Rating]: reviewFilters[ReviewsAndAnswersFilterQueriesEnum.Rating].join(','),
        [ReviewsAndAnswersFilterQueriesEnum.WithoutText]: reviewFilters[ReviewsAndAnswersFilterQueriesEnum.WithoutText],
        [ReviewsAndAnswersFilterQueriesEnum.IsReplied]: reviewFilters[ReviewsAndAnswersFilterQueriesEnum.IsReplied],
        [ReviewsAndAnswersFilterQueriesEnum.ActionReply]: reviewFilters[ReviewsAndAnswersFilterQueriesEnum.ActionReply],
        [ReviewsAndAnswersFilterQueriesEnum.Target]: reviewFilters[ReviewsAndAnswersFilterQueriesEnum.Target],
        [ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted]: reviewFilters[
            ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted
        ]
            ? 'true'
            : '',
    };
}

// Picks the review-filter subset out of the full filter state.
export function getReviewFilters(filter: ReviewsAndAnswersFilterStateType): ReviewsAndAnswersReviewFiltersType {
    return {
        [ReviewsAndAnswersFilterQueriesEnum.CatalogIds]: filter[ReviewsAndAnswersFilterQueriesEnum.CatalogIds],
        [ReviewsAndAnswersFilterQueriesEnum.Sentiments]: filter[ReviewsAndAnswersFilterQueriesEnum.Sentiments],
        [ReviewsAndAnswersFilterQueriesEnum.TagIds]: filter[ReviewsAndAnswersFilterQueriesEnum.TagIds],
        [ReviewsAndAnswersFilterQueriesEnum.Rating]: filter[ReviewsAndAnswersFilterQueriesEnum.Rating],
        [ReviewsAndAnswersFilterQueriesEnum.WithoutText]: filter[ReviewsAndAnswersFilterQueriesEnum.WithoutText],
        [ReviewsAndAnswersFilterQueriesEnum.IsReplied]: filter[ReviewsAndAnswersFilterQueriesEnum.IsReplied],
        [ReviewsAndAnswersFilterQueriesEnum.ActionReply]: filter[ReviewsAndAnswersFilterQueriesEnum.ActionReply],
        [ReviewsAndAnswersFilterQueriesEnum.Target]: filter[ReviewsAndAnswersFilterQueriesEnum.Target],
        [ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted]: filter[ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted],
    };
}

// Number of active review-filter groups (drives the filter button badge).
export function getActiveReviewFiltersCount(reviewFilters: ReviewsAndAnswersReviewFiltersType): number {
    return [
        reviewFilters[ReviewsAndAnswersFilterQueriesEnum.CatalogIds].length > 0,
        reviewFilters[ReviewsAndAnswersFilterQueriesEnum.Sentiments].length > 0,
        reviewFilters[ReviewsAndAnswersFilterQueriesEnum.TagIds].length > 0,
        reviewFilters[ReviewsAndAnswersFilterQueriesEnum.Rating].length > 0,
        Boolean(reviewFilters[ReviewsAndAnswersFilterQueriesEnum.WithoutText]),
        Boolean(reviewFilters[ReviewsAndAnswersFilterQueriesEnum.IsReplied]),
        Boolean(reviewFilters[ReviewsAndAnswersFilterQueriesEnum.ActionReply]),
        Boolean(reviewFilters[ReviewsAndAnswersFilterQueriesEnum.Target]),
        reviewFilters[ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted] !==
            defaultReviewFilters[ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted],
    ].filter(Boolean).length;
}
